import { ArrowBack, SaveOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import * as categoryActions from "../../../../redux/categories/categories.actions";
import * as categoryReducers from "../../../../redux/categories/categories.reducers";
import * as productActions from "../../../../redux/products/products.actions";
import * as productReducers from "../../../../redux/products/products.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import Button3D from "../../../../utils/common/3d-button/Button3D";
import { UploadImageWidget } from "../../../../utils/UploadImageWidget";
import { ISubCategoryView } from "../../../categories/models/ICategoryResponseView";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { primary } from "../../../../themes/theme";

const validationSchema = Yup.object().shape({
  categoryId: Yup.string().required("Category is required"),
  subCategoryId: Yup.string().required("Sub Category is required"),
  title: Yup.string().trim().required("Product Name is required"),
  description: Yup.string().trim().required("Description is required"),
  imageUrl: Yup.string().trim().required("Image URL is required"),
  brand: Yup.string().trim().required("Brand is required"),
  price: Yup.number().required("Price is required"),
  quantity: Yup.number().required("Quantity is required"),
});

export function ProductPreviewText(props: any) {
  return (
    <Grid
      container
      spacing={1}
      sx={{
        display: "flex",
      }}
    >
      <Grid item xs={2}>
        <Typography
          variant="body1"
          gutterBottom
          sx={{
            fontFamily: "Ubuntu",
            fontWeight: "550",
          }}
        >
          {props.primary}
        </Typography>
      </Grid>
      <Grid item xs={10}>
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            fontFamily: "Ubuntu",
            fontWeight: "500",
          }}
        >
          - {props.secondary}
        </Typography>
      </Grid>
    </Grid>
  );
}

const EditProduct: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [categoryId, setCategoryId] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("fashion");
  const [subCategoryId, setSubCategoryId] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [subCategories, setSubCategories] = useState<ISubCategoryView[]>([]);
  const cloudinaryRef = useRef<any>();
  const widgetRef = useRef<any>();

  // Get Products Data from Redux Store
  const productsReduxState: productReducers.InitialState = useSelector(
    (state: RootState) => {
      return state[productReducers.productFeatureKey];
    }
  );

  const { loading, product } = productsReduxState;

  const handleUploadImage = () => {
    widgetRef.current.open();
  };

  // Get all Categories from redux
  const categoryReduxState: categoryReducers.InitialState = useSelector(
    (state: RootState) => state[categoryReducers.categoryFeatureKey]
  );

  const { loading: categoryLoading, categories } = categoryReduxState;

  useEffect(() => {
    dispatch(categoryActions.getAllCategoriesAction());
    UploadImageWidget.upload(cloudinaryRef, widgetRef)
      .then((imageUrl) => {
        if (imageUrl) {
          setImageUrl(imageUrl.toString());
          formik.setFieldValue("imageUrl", imageUrl.toString());
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, [dispatch]);

  useEffect(() => {
    if (productId) {
      dispatch(productActions.getProductAction({ productId: productId }));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    if (product) {
      const selectedCategoryId = product.categoryObj?._id || "";
      const selectedSubCategoryId = product.subCategoryObj?._id || "";
      setCategoryId(selectedCategoryId);
      setSubCategoryId(selectedSubCategoryId);

      // Fetch subcategories for the selected category
      const selectedCategory = categories.find(
        (category) => category._id === selectedCategoryId
      );
      setCategoryName(selectedCategory?.name || "");
      setSubCategories(selectedCategory?.subCategories || []);

      // Set initial formik values
      formik.setValues({
        title: product.title || "",
        description: product.description || "",
        imageUrl: product.imageUrl || "",
        brand: product.brand || "",
        price: product.price || 0,
        quantity: product.quantity || 0,
        categoryId: selectedCategoryId,
        subCategoryId: selectedSubCategoryId,
      });
    }
  }, [product, categories]);

  const handleCategoryChange = (e: any) => {
    const selectedCategoryId = e.target.value;
    setCategoryId(selectedCategoryId);

    const selectedCategory = categories.find(
      (category) => category._id === selectedCategoryId
    );
    setCategoryName(selectedCategory?.name || "");

    formik.setFieldValue("categoryId", selectedCategoryId);

    // Reset subCategoryId to prevent out-of-range warning
    setSubCategoryId("");

    // Fetch subcategories for the selected category
    if (selectedCategory && selectedCategory.subCategories) {
      setSubCategories(selectedCategory.subCategories);
    } else {
      setSubCategories([]);
    }
  };

  const handleSubCategoryChange = (e: any) => {
    const selectedSubCategoryId = e.target.value;
    setSubCategoryId(selectedSubCategoryId);
    formik.setFieldValue("subCategoryId", selectedSubCategoryId);
  };

  const handleUpdateProduct = async (values: any) => {
    if (productId) {
      const response = await dispatch(
        productActions.udpateProductAction({
          product: values,
          productId: productId,
        })
      );

      if (response && !response.error) {
        navigate(`/products/${categoryName.toLowerCase()}`);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      title: product?.title || "",
      description: product?.description || "",
      imageUrl: product?.imageUrl || "",
      brand: product?.brand || "",
      price: product?.price || "",
      quantity: product?.quantity || "",
      categoryId: product?.categoryObj?._id || "",
      subCategoryId: product?.subCategoryObj?._id || "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleUpdateProduct(values);
    },
  });

  return (
    <>
      <Box sx={{ mb: 2, pb: 2 }}>
        {(loading || categoryLoading) && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="Edit Product"
          description="Easily update product information, pricing, and inventory for accurate listings."
        />
        <Grid
          container
          sx={{ gap: "10px", pl: { xs: 1, md: 9, lg: 10 }, pr: 1 }}
        >
          <Grid item xs={12}>
            <ArrowBack
              onClick={() => navigate("/products/admin")}
              sx={{
                color: "grey",
                cursor: "pointer",
                "&:hover": { color: "black" },
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            component={Paper}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "10px",
              height: "100%",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Ubuntu",
                  fontSize: "17px",
                  letterSpacing: "1px",
                  fontWeight: "550",
                }}
              >
                | Edit Product Details
              </Typography>
            </Box>
            <Divider
              sx={{
                mt: 1,
                mb: 2,
                color: primary[900],
                border: "1px dashed #f1f2f3",
              }}
            />
            <form onSubmit={formik.handleSubmit}>
              <FormControl fullWidth>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  sx={{
                    mb: 2,
                    textTransform: "capitalize",
                    border: "1px dashed grey",
                  }}
                  onClick={() => handleUploadImage()}
                >
                  Change Image
                </Button>
                {formik.touched.imageUrl && formik.errors.imageUrl && (
                  <FormHelperText error>
                    {formik.errors.imageUrl}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                error={
                  formik.touched.categoryId && Boolean(formik.errors.categoryId)
                }
              >
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="categoryId"
                  label="Category"
                  name="categoryId"
                  value={formik.values.categoryId}
                  onChange={(e) => {
                    handleCategoryChange(e);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                >
                  {categories?.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && (
                  <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                )}
              </FormControl>
              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                error={
                  formik.touched.subCategoryId &&
                  Boolean(formik.errors.subCategoryId)
                }
              >
                <InputLabel id="sub-category-label">Sub Category</InputLabel>
                <Select
                  labelId="sub-category-label"
                  id="subCategoryId"
                  label="Sub Category"
                  name="subCategoryId"
                  value={formik.values.subCategoryId}
                  onChange={(e) => {
                    handleSubCategoryChange(e);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                >
                  {subCategories?.map((subCategory) => (
                    <MenuItem key={subCategory._id} value={subCategory._id}>
                      {subCategory.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.subCategoryId && (
                  <FormHelperText>{formik.errors.subCategoryId}</FormHelperText>
                )}
              </FormControl>
              <TextField
                label="Product Name"
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
              <TextField
                label="Brand"
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                name="brand"
                value={formik.values.brand}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.brand && Boolean(formik.errors.brand)}
                helperText={formik.touched.brand && formik.errors.brand}
              />
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />

              <TextField
                label="Price"
                variant="outlined"
                fullWidth
                type="number"
                sx={{ mb: 2 }}
                name="price"
                value={formik.values.price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
              />
              <TextField
                label="Quantity"
                variant="outlined"
                fullWidth
                type="number"
                sx={{ mb: 2 }}
                name="quantity"
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.quantity && Boolean(formik.errors.quantity)
                }
                helperText={formik.touched.quantity && formik.errors.quantity}
              />
              <Button3D
                buttonText="Update Product"
                buttonIcon={<SaveOutlined sx={{ pr: 1, mt: "3px" }} />}
                type="submit"
              />
            </form>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                px: 2,
                borderRadius: "10px",
              }}
            >
              <CardContent>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu",
                    fontSize: "17px",
                    letterSpacing: "1px",
                    fontWeight: "550",
                  }}
                >
                  | Product Preview
                </Typography>
                <Divider
                  sx={{
                    my: 1,
                    color: primary[900],
                    border: "1px dashed #f1f2f3",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    mb: 2,
                  }}
                >
                  <img
                    src={imageUrl || product?.imageUrl}
                    alt="product"
                    style={{
                      width: "225px",
                      height: "250px",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                  />
                </Box>
                <ProductPreviewText
                  primary={"Name"}
                  secondary={formik.values.title}
                />
                <ProductPreviewText
                  primary={"Brand"}
                  secondary={formik.values.brand}
                />

                <ProductPreviewText
                  primary={"Category"}
                  secondary={
                    categories.find(
                      (cat) => cat._id === formik.values.categoryId
                    )?.name
                  }
                />
                <ProductPreviewText
                  primary={"Sub Category"}
                  secondary={
                    subCategories.find(
                      (sub) => sub._id === formik.values.subCategoryId
                    )?.name
                  }
                />
                <ProductPreviewText
                  primary={"Price"}
                  secondary={`₹${formik.values.price}`}
                />
                <ProductPreviewText
                  primary={"Quantity"}
                  secondary={formik.values.quantity}
                />
                <ProductPreviewText
                  primary={"Description"}
                  secondary={formik.values.description}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default EditProduct;
